
const DEFAULT_APP_NAME = 'rp-app';
const DEFAULT_APP_BASE = '/gaialightapps/';

const JS_FILE_SUFFIX = '.js';
const CSS_FILE_SUFFIX = '.css';

const CLASS_NEW = 'gaia-state-new';
const CLASS_INITIALISING = 'gaia-state-initializing';
const CLASS_INITIALISED = 'gaia-state-initialized';
const CLASS_ERROR = 'gaia-state-error';

const INITIALISE_FUNCTION_PREFIX = 'glInit';

const LOADED_FILES: {[index: string]: HTMLLinkElement|HTMLScriptElement} = {};

/**
 * Function to initialise GAIA-MVlight App in the given Element
 *
 * @param hostElement Element to contain the GAIA-MVlight App
 */
export function gaiaInit(hostElement: HTMLElement): void {
    if (!hostElement) {
        console.error('Element required for gaiaInit');
        return;
    }
    const debugOutput: boolean = document.querySelector<HTMLBodyElement>('body')?.dataset.debug !== undefined;
    const debug: (...args: any[]) => void = debugOutput ? console.debug : () => undefined;
    const data: DOMStringMap = hostElement.dataset;
    const classes: DOMTokenList = hostElement.classList;
    if (!classes.contains(CLASS_NEW)) {
        console.warn('Element does not contain class ' + CLASS_NEW + '... skipping.', hostElement);
        return;
    }
    if (classes.contains(CLASS_INITIALISING)) {
        debug('App is already being initialised, skipping.', hostElement);
        return;
    }
    classes.add(CLASS_INITIALISING);
    if (classes.contains(CLASS_INITIALISED)) {
        debug('App is already initialised, skipping.', hostElement);
    }

    const glApp: string = data.gaiaApp || DEFAULT_APP_NAME;
    const glBase: string|undefined = data.gaiaBaseUrl;

    const baseUrl: string = (glBase || (DEFAULT_APP_BASE + glApp + '/')) + glApp;

    const cssUrl: string = baseUrl + CSS_FILE_SUFFIX;
    const jsUrl: string = baseUrl + JS_FILE_SUFFIX;

    const head: HTMLHeadElement|null = document.querySelector<HTMLHeadElement>('head');

    if (LOADED_FILES[cssUrl]) {
        debug('Already loaded ' + cssUrl + '... skipping');
    } else {
        const link: HTMLLinkElement = document.createElement('link');
        link.setAttribute('type', 'text/css');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('href', cssUrl);
        head?.appendChild(link);
        LOADED_FILES[cssUrl] = link;
        debug('Loading ' + cssUrl);
    }

    let jsElement: HTMLScriptElement|undefined = LOADED_FILES[jsUrl] as HTMLScriptElement|undefined;

    /**
     * Function to initialse the app.
     *
     * This may happen automatically through the script, or it may require
     * calling a function which has been loaded.
     */
    const initialiseApp: () => void = (): void => {
        const functionName: string = INITIALISE_FUNCTION_PREFIX + glApp.split(/[^A-Z]+/ig).map((part: string): string => {
            if (part.length < 1) {
                return '';
            }
            return part.substr(0, 1).toUpperCase() + part.substr(1);
        }).join('');
        // @ts-ignore We need to test whether this is a function
        if ('function' === typeof window[functionName]) {
            debug('Calling initialisation function ' + functionName + ' for element', hostElement);
            try {
                // @ts-ignore We now know that this is a function, so call it with the element
                window[functionName](hostElement);
            } catch (e) {
                console.error(e);
                classes.add(CLASS_ERROR);
            }
        } else {
            debug('Initialisation function not present; skipping: ' + functionName);
        }
        classes.remove(CLASS_NEW);
        classes.remove(CLASS_INITIALISING);
        classes.add(CLASS_INITIALISED);
    };

    /**
     * Callback function when script element successfully loaded
     */
    const scriptLoaded: () => void = (): void => {
        debug('Successfully loaded script ' + jsUrl);
        scriptLoadComplete();
        initialiseApp();
    };

    /**
     * Callback function when script element cannot load
     */
    const scriptError: () => void = (): void => {
        debug('Error loading script ' + jsUrl);
        if (jsElement) {
            jsElement.dataset.error = 'true';
        }
        scriptLoadComplete();
        classes.remove(CLASS_NEW);
        classes.remove(CLASS_INITIALISING);
        classes.add(CLASS_ERROR);
    }

    /**
     * Callback function when script element loading is complete
     */
    const scriptLoadComplete: () => void = (): void => {
        if (jsElement) {
            jsElement.dataset.loading = undefined;
            jsElement.removeEventListener('load', scriptLoaded);
            jsElement.removeEventListener('error', scriptError);
        }
    }

    if (jsElement) {
        // script element exists, need to check if script already loaded / run
        const jsData: DOMStringMap = jsElement.dataset;
        if (jsData.loading === 'true') {
            // script still loading, so append our own event listeners
            jsElement.addEventListener('load', scriptLoaded);
            jsElement.addEventListener('error', scriptError);
            debug('Added event handlers to existing JS element', jsElement);
        } else if (jsData.error) {
            // script did not load correctly: not much we can do here
            debug('Script file did not load correctly, skipping... ' + jsUrl);
        } else {
            // script already loaded, try running
            debug('Script ' + jsUrl + ' already loaded...');
            initialiseApp();
        }
    } else {
        jsElement = document.createElement('script');
        jsElement.dataset.loading = 'true';
        jsElement.addEventListener('load', scriptLoaded);
        jsElement.addEventListener('error', scriptError);
        head?.appendChild(jsElement);
        jsElement.src = jsUrl;
    }
}
